import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deviceRegistry } from "../../services/deviceRegistry";
import styles from "./AddDevice.module.css";

const AddDevice = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [macId, setMacId] = useState("");
  const [entryMethod, setEntryMethod] = useState<"manual" | "barcode" | "url">(
    "manual"
  );
  const [error, setError] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [detectedType, setDetectedType] = useState<string | null>(null);

  // Validate MAC address format
  const isValidMacAddress = (mac: string): boolean => {
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    return macRegex.test(mac);
  };

  // Format MAC address as user types
  const formatMacAddress = (value: string): string => {
    // Remove all non-hex characters
    const cleaned = value.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();

    // Add colons every 2 characters
    const formatted = cleaned.match(/.{1,2}/g)?.join(":") || cleaned;

    // Limit to 17 characters (12 hex + 5 colons)
    return formatted.substring(0, 17);
  };

  const handleMacChange = (value: string) => {
    const formatted = formatMacAddress(value);
    setMacId(formatted);
    setError("");

    // Try to detect device type as user types
    if (formatted.length >= 8) {
      const type = deviceRegistry.detectDeviceType(formatted);
      setDetectedType(type || null);
    } else {
      setDetectedType(null);
    }
  };

  const handleContinue = () => {
    if (!macId) {
      setError(t('addDevice.errorEmpty'));
      return;
    }

    if (!isValidMacAddress(macId)) {
      setError(t('addDevice.errorInvalidFormat'));
      return;
    }

    // Detect device type using registry
    const deviceType = deviceRegistry.detectDeviceType(macId);

    if (!deviceType) {
      setError(
        t('addDevice.errorNotRecognized')
      );
      return;
    }

    const wizardRoute = deviceRegistry.getWizardRoute(deviceType);

    if (!wizardRoute) {
      setError(
        `No setup wizard available for ${deviceRegistry.getDisplayName(deviceType)}`
      );
      return;
    }

    // Navigate to device-specific wizard via loader
    navigate(`${wizardRoute}?mac=${encodeURIComponent(macId)}&from=shell`);
  };

  const handleBarcodeScan = () => {
    setEntryMethod("barcode");
    setIsScanning(true);
    setError("");

    // Simulate barcode scanning (in production, this would open camera/scanner)
    setTimeout(() => {
      // Mock scanned MAC
      const scannedMac = "AA:BB:CC:DD:EE:05";
      setMacId(scannedMac);
      setIsScanning(false);
      setError("");
    }, 2000);
  };

  const handleUrlParse = () => {
    setEntryMethod("url");

    // Mock URL parsing (in production, would read from QR code or NFC)
    const mockUrl = "https://device.setup/camera?mac=AA:BB:CC:DD:EE:06";

    try {
      const url = new URL(mockUrl);
      const macFromUrl = url.searchParams.get("mac");

      if (macFromUrl) {
        setMacId(formatMacAddress(macFromUrl));
        setError("");
      } else {
        setError("No MAC ID found in URL");
      }
    } catch {
      setError("Invalid URL format");
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.wizard}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div>
            <h1 className={styles.title}>{t('addDevice.title')}</h1>
            <p className={styles.subtitle}>{t('addDevice.stepOne')}</p>
          </div>
        </div>

        {/* Entry Method Selection */}
        <div className={styles.methodSelection}>
          <button
            className={`${styles.methodButton} ${entryMethod === "manual" ? styles.methodButtonActive : ""
              }`}
            onClick={() => setEntryMethod("manual")}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 7h16M4 12h16M4 17h10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span>{t('addDevice.manualEntry')}</span>
          </button>

          <button
            className={`${styles.methodButton} ${entryMethod === "barcode" ? styles.methodButtonActive : ""
              }`}
            onClick={handleBarcodeScan}
            disabled={isScanning}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 5v14h18V5H3zm2 2h2v10H5V7zm3 0h1v10H8V7zm2 0h2v10h-2V7zm3 0h1v10h-1V7zm2 0h2v10h-2V7zm3 0h1v10h-1V7z"
                fill="currentColor"
              />
            </svg>
            <span>{isScanning ? t('addDevice.scanning') : t('addDevice.scanBarcode')}</span>
          </button>

          <button
            className={`${styles.methodButton} ${entryMethod === "url" ? styles.methodButtonActive : ""
              }`}
            onClick={handleUrlParse}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{t('addDevice.scanUrlQr')}</span>
          </button>
        </div>

        {/* MAC ID Input */}
        <div className={styles.inputSection}>
          <label className={styles.label}>{t('addDevice.macIdLabel')}</label>
          <input
            type="text"
            className={styles.input}
            placeholder={t('addDevice.macIdPlaceholder')}
            value={macId}
            onChange={(e) => handleMacChange(e.target.value)}
            disabled={isScanning}
          />
          <p className={styles.hint}>
            {t('addDevice.macIdHint')}
          </p>
          {error && <p className={styles.error}>{error}</p>}
        </div>

        {/* Device Info Preview */}
        {macId && isValidMacAddress(macId) && !error && detectedType && (
          <div className={styles.devicePreview}>
            <div className={styles.previewIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <rect
                  x="2"
                  y="6"
                  width="16"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M18 10l4-2v8l-4-2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </div>
            <div>
              <h3 className={styles.previewTitle}>{t('addDevice.deviceDetected')}</h3>
              <p className={styles.previewType}>
                {t('addDevice.type')} {deviceRegistry.getDisplayName(detectedType)}
              </p>
              <p className={styles.previewMac}>{t('addDevice.mac')} {macId}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className={styles.actions}>
          <button className={styles.cancelButton} onClick={() => navigate(-1)}>
            {t('addDevice.cancel')}
          </button>
          <button
            className={styles.continueButton}
            onClick={handleContinue}
            disabled={!macId || isScanning}
          >
            {t('addDevice.continueToSetup')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDevice;
